import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, LogOut, Moon, Sun } from 'lucide-react';
import NotificationCenter from '../components/NotificationCenter';
import NetWorthTracker from '../components/dashboard/NetWorthTracker';
import DataMigration from '../components/common/DataMigration';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { useTheme } from '../contexts/ThemeContext';
import { useInitialDataSetup } from '../hooks/useInitialDataSetup';
import { useMediaQuery } from '../hooks/useMediaQuery';
import DemoBanner from '../components/demo/DemoBanner';
import ConversionModal from '../components/demo/ConversionModal';

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { isDemo, showConversionPrompt, dismissConversionPrompt } = useDemo();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showMigration, setShowMigration] = useState(false);
  const [localData, setLocalData] = useState(null);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const { isInitializing } = useInitialDataSetup();
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    // Check for local storage data on mount
    const saved = localStorage.getItem('financeData');
    const migrated = localStorage.getItem('dataMigrated');
    
    if (saved && !migrated) {
      const data = JSON.parse(saved);
      const currentYear = new Date().getFullYear();
      
      // Only show migration if there's data for the current year
      if (data.years && data.years[currentYear]) {
        setLocalData(data);
        setShowMigration(true);
      }
    }
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleMigrationComplete = () => {
    setShowMigration(false);
    window.location.reload(); // Reload to fetch the migrated data
  };

  // Show conversion modal when prompted
  useEffect(() => {
    if (showConversionPrompt && isDemo) {
      setShowConversionModal(true);
    }
  }, [showConversionPrompt, isDemo]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100 bg-canvas flex flex-col">
      {/* Demo Banner */}
      {isDemo && <DemoBanner />}
      
      {/* Conversion Modal */}
      <ConversionModal 
        isOpen={showConversionModal} 
        onClose={() => {
          setShowConversionModal(false);
          dismissConversionPrompt();
        }}
      />
      
      {/* Top Navigation Bar */}
      <div className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="hover:opacity-80 transition-opacity"
                title="Go to Dashboard"
              >
                <Logo size={isMobile ? "small" : "default"} />
              </button>
            </div>
            
            {/* Desktop Navigation */}
            {!isMobile && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Welcome, {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                </span>
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 rounded-lg transition-colors opacity-70 hover:opacity-100 focus:opacity-100"
                  title="Toggle theme"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Profile Settings"
                >
                  <User className="w-5 h-5" />
                </button>
                <NotificationCenter className="text-gray-600 dark:text-gray-300" />
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Mobile Navigation Icons */}
            {isMobile && (
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-500 dark:text-gray-400 rounded-lg"
                  title="Toggle theme"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2 text-gray-500 dark:text-gray-400 rounded-lg"
                  title="Profile"
                >
                  <User className="w-5 h-5" />
                </button>
                <NotificationCenter className="text-gray-500 dark:text-gray-400" />
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-500 dark:text-gray-400 rounded-lg"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow">
        {isInitializing ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Setting up your financial dashboard...</p>
            </div>
          </div>
        ) : (
          <NetWorthTracker />
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 mt-auto">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Mobile Footer - Compact single line */}
          {isMobile ? (
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-3">
                <Link to="/terms" className="hover:text-blue-600">Terms</Link>
                <span>•</span>
                <Link to="/privacy" className="hover:text-blue-600">Privacy</Link>
              </div>
              <span>© {new Date().getFullYear()} Techbrov</span>
            </div>
          ) : (
            /* Desktop Footer - Full layout */
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Logo and Description */}
              <div className="flex items-center gap-3">
                <Logo size="small" />
                <span className="text-sm text-gray-600 dark:text-gray-300">Secure financial management platform</span>
              </div>
              
              {/* Legal Links and Copyright */}
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
                <Link 
                  to="/terms" 
                  className="hover:text-blue-600 transition-colors"
                >
                  Terms
                </Link>
                <span className="text-gray-400">•</span>
                <Link 
                  to="/privacy" 
                  className="hover:text-blue-600 transition-colors"
                >
                  Privacy
                </Link>
                <span className="text-gray-400">•</span>
                <span>© {new Date().getFullYear()} Techbrov. All rights reserved.</span>
              </div>
            </div>
          )}
        </div>
      </footer>

      {/* Data Migration Modal */}
      {showMigration && localData && (
        <DataMigration
          localData={localData}
          onClose={() => setShowMigration(false)}
          onComplete={handleMigrationComplete}
        />
      )}
    </div>
  );
};

export default Dashboard;